import { createFeatureSelector, createSelector } from '@ngrx/store';
import { getError, LoadingState } from 'app/utils/callstate';
import { sortBy, find } from 'lodash';

import { CompaniesState, Period, formatStartDate, displayStartDate } from '../company.types';
import { RouteState, State } from '../reducers';
import { selectAll, selectEntities } from '../reducers/companies.reducer';
import { selectCurrentCompanyId, selectCurrentPeriod } from './companies.ui.selectors';
import { KeyStringFilterInput } from 'app/api/common.api.types';

export const getRouteState = createFeatureSelector<State, RouteState>('company');

export const selectCompaniesState = createSelector(
    getRouteState,
    ({ companies }): CompaniesState => companies
);

export const selectCompaniesEntities = createSelector(
    selectCompaniesState,
    selectEntities
);

export const selectCurrentCompany = createSelector(
    selectCompaniesEntities,
    selectCurrentCompanyId,
    (companies, companyId) => companies[companyId]
);

export const selectCurrentAccountingYears = createSelector(
    selectCurrentCompany,
    company => company ? sortBy(company.accountingYears, 'startDate') : []
);

export const selectCurrentPossiblePeriods = createSelector(
    selectCurrentAccountingYears,
    accountingYears => {
        const periods: Period[] = accountingYears.map(year => ({ from: year }));
        if (accountingYears.length > 1) {
            accountingYears.forEach((year, g) => {
                for (let d = g + 1; d < accountingYears.length; d++) {
                    periods.push({ from: year, to: accountingYears[d]});
                }    
            });    
        }
        return periods;
    }
);

export const selectCurrentCompanyPeriods = createSelector(
    selectCurrentPossiblePeriods,
    (periods): Period[] => periods.map(period => {
        const from = formatStartDate(period.from);
        const to = formatStartDate(period.to);
        const isSinglePeriod = (to === '');
        const key = isSinglePeriod
            ? from
            : `${from}|${to}included`;
        const value = isSinglePeriod
            ? displayStartDate(period.from)
            : `${displayStartDate(period.from)} - ${displayStartDate(period.to)}`;
        const keyStringFilterInput: KeyStringFilterInput = isSinglePeriod
            ? { beginsWith: key }
            : { between: key };
        return { from: period.from, to: period.to, key, value, keyStringFilterInput };
    })
);

export const selectAllCompanies = createSelector(
    selectCompaniesState,
    selectAll
);

export const selectAllCompaniesSorted = createSelector(
    selectAllCompanies,
    companies => sortBy(companies, 'name')
);

export const selectCompaniesLoading = createSelector(
    selectCompaniesState,
    companiesState => companiesState.callState === LoadingState.Loading
);

export const selectCompaniesLoaded = createSelector(
    selectCompaniesState,
    companiesState => companiesState.callState === LoadingState.Loaded
);

export const selectCompaniesError = createSelector(
    selectCompaniesState,
    companiesState => getError(companiesState.callState)
);
